import { ParsedQuery } from "query-string";
import React from "react";

import { IQueryParams, navigator } from "shared/services/navigator";

import { Diff } from "shared/types/utilities";

export interface IWithQueryParameters<TQueryParameters> {
  queryParameters: TQueryParameters;
}

export type ExtendedQuery<TRawQueryParameters> = ParsedQuery &
  TRawQueryParameters;

type HocProps<TProps extends object, TQueryParameters> = Diff<
  TProps,
  IWithQueryParameters<TQueryParameters>
>;

export type QueryParser<TParsedQueryParameters, TRawQueryParameters> = (
  query: ExtendedQuery<TRawQueryParameters>
) => TParsedQueryParameters;

export type QueryStringifier<TRawQueryParameters, TParsedQueryParameters> = (
  params: TParsedQueryParameters
) => IQueryParams & TRawQueryParameters;

export default function withQueryParametersFactory<
  TParsedQueryParameters,
  TRawQueryParameters = ParsedQuery,
>(parseQuery: QueryParser<TParsedQueryParameters, TRawQueryParameters>) {
  return function withQueryParameters<TProps extends {}>(
    WrappedComponent: React.ComponentType<React.PropsWithChildren<TProps>>
  ) {
    return class extends React.Component<
      HocProps<TProps, TParsedQueryParameters>
    > {
      public render() {
        return (
          <WrappedComponent
            {...(this.props as TProps)}
            queryParameters={this.getQueryParameters()}
          />
        );
      }

      private getQueryParameters = () =>
        parseQuery(
          navigator.getCurrentQuery() as ExtendedQuery<TRawQueryParameters>
        );
    };
  };
}
